/**
 * LostDevicesTable — communication lost (offline devices) table.
 */

import { Link } from 'react-router-dom';
import { convertIsoToLocal } from '../../../../libs.jsx';
import { getRoleWeight } from '../../../../services/session.js';
import { getAlertDeviceLink } from '../../../../utils/deviceLinks.js';
import SortableTh from '../../../../components/v2/SortableTh.jsx';

const LostDevicesTable = ({ data, sortColumn, sortOrder, onSort }) => {
    const sortIcon = (col) => {
        if (sortColumn !== col) return null;
        return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>;
    };

    /* Shared with the Alerts table (utils/deviceLinks.js). This copy used to map only
       manufacturers 1 and 4, so a server-offline alert opened a PalCare device
       profile for the wrong record, and wandering devices never linked. */
    const getDeviceLink = (row) => getAlertDeviceLink(row, getRoleWeight());

    const renderResidents = (row) => {
        const residents = row.residents;
        if (!residents || !Array.isArray(residents) || residents.length === 0) return '—';
        return residents.map((r, i) => (
            <span key={i}>
                {r.resident_id ? (
                    <Link to={`/profiles/resident/${r.resident_id}`} className="ar-link">{r.resident_name || '—'}</Link>
                ) : (r.resident_name || '—')}
                {i < residents.length - 1 ? ', ' : ''}
            </span>
        ));
    };

    return (
        <div className="dp-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="dp-table">
                <thead className="dp-thead">
                    <tr>
                        <SortableTh sortKey="last_active" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Last Active{sortIcon('last_active')}</SortableTh>
                        <SortableTh sortKey="device_name" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Name{sortIcon('device_name')}</SortableTh>
                        <SortableTh sortKey="descriptions" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Description{sortIcon('descriptions')}</SortableTh>
                        <SortableTh sortKey="manufacture_uuid" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Serial Number{sortIcon('manufacture_uuid')}</SortableTh>
                        <th className="dp-th">Residents</th>
                        <SortableTh sortKey="area_name" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Area{sortIcon('area_name')}</SortableTh>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr className="dp-tr">
                            <td className="dp-td" colSpan={6}>
                                <div className="na-empty">
                                    <div className="na-empty__icon">✓</div>
                                    <div className="na-empty__text">No devices with communication lost</div>
                                </div>
                            </td>
                        </tr>
                    ) : null}
                    {data.map((row, idx) => {
                        const link = getDeviceLink(row);
                        return (
                            <tr key={row.device_id || idx} className="dp-tr">
                                <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>
                                    {row.last_active ? (convertIsoToLocal(row.last_active) || row.last_active) : '—'}
                                </td>
                                <td className="dp-td">
                                    {link ? (
                                        <Link to={link} className="ar-link">{row.device_name || '—'}</Link>
                                    ) : (row.device_name || '—')}
                                </td>
                                <td className="dp-td">{row.descriptions || '—'}</td>
                                <td className="dp-td" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>{row.manufacture_uuid || '—'}</td>
                                <td className="dp-td">{renderResidents(row)}</td>
                                <td className="dp-td">
                                    {row.area_id ? (
                                        <Link to={`/profiles/area/${row.area_id}`} className="ar-link">{row.area_name || '—'}</Link>
                                    ) : (row.area_name || '—')}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default LostDevicesTable;
